import React from 'react';
import moment from 'moment';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import NoDataIcon from '../../../Assets/ill-no-data.svg';
import MobileIcon from '../dashboardV3/mobile-homeindicator.svg';
import DesktopIcon from '../dashboardV3/monitor-tv.svg';

// platform "device" = mobile app; anything else (e.g. "windows") = laptop/desktop.
const getDeviceVisual = (platform) => {
    const isMobile = String(platform || '').toLowerCase() === 'device';
    return isMobile
        ? { icon: MobileIcon, bg: "#D8F0FF" }
        : { icon: DesktopIcon, bg: "#FFDEDF" };
};

const ActivityTab = ({
    userActivityLoading,
    flatActivities,
    activityStats,
    isStaffRoute,
    isTeacher,
}) => {
    return (
        <div style={{ display: "flex", gap: fluid(24), alignItems: "stretch", flex: 1, minHeight: 0 }}>
            {/* Left Scrollable Column (Activity Timeline) */}
            <div
                className="staff-profile-info-scroll"
                style={{
                    flex: `1 1 ${fluid(882)}`,
                    minWidth: fluid(882),
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: fluid(24),
                    overflowY: "auto",
                    overflowX: "hidden",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                }}
            >
                <div style={{ width: "100%", backgroundColor: "#FFFFFF", borderRadius: fluid(24), padding: fluid(24), boxSizing: "border-box", display: "flex", flexDirection: "column", gap: fluid(24), flex: "1 0 auto" }}>
                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(20), fontWeight: 500, color: "#191919" }}>
                        Activity timeline
                    </span>

                    {userActivityLoading ? null : flatActivities.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: `${fluid(40)} 0`,
                            width: '100%'
                        }}>
                            <img src={NoDataIcon} style={{ width: fluid(250) }} alt="No activity found" />
                            <span style={{ fontSize: fluid(18), fontWeight: 500, color: '#636363', marginTop: fluid(16), fontFamily: 'Roboto, sans-serif' }}>
                                No activity found
                            </span>
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", position: "relative", paddingLeft: fluid(16) }}>
                            {/* Timeline Connector Line */}
                            <div style={{
                                position: "absolute",
                                left: fluid(96),
                                top: fluid(24),
                                bottom: fluid(24),
                                width: "1px",
                                backgroundColor: "#E6E6E6",
                                zIndex: 0,
                            }} />

                            {/* Timeline Entries */}
                            {flatActivities.map((entry, idx) => {
                                const d = moment(entry.date);
                                const isValid = d.isValid();
                                const dateNum = isValid ? d.format('DD') : '';
                                const dateMonth = isValid ? d.format('MMM') : '';
                                const subtitle = isValid ? d.format('DD MMM YYYY, h:mm A') : '';
                                const dotColor = (entry.type === 'login' || entry.type === 'logout') ? '#FF595E' : (entry.type === 'general' ? '#1982C4' : '#8AC926');

                                return (
                                    <div key={entry.id || entry.userId || idx} style={{ display: "flex", alignItems: "flex-start", gap: fluid(24), minHeight: fluid(48), marginBottom: idx === flatActivities.length - 1 ? 0 : fluid(32), position: "relative", zIndex: 1 }}>
                                        {/* Date Badge */}
                                        {dateNum ? (
                                            <div style={{ width: fluid(48), height: fluid(48), borderRadius: fluid(12), backgroundColor: "#F4F4F4", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(12), color: "#666666", fontWeight: 400, lineHeight: fluid(14) }}>{dateNum}</span>
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(12), color: "#666666", fontWeight: 400, lineHeight: fluid(14) }}>{dateMonth}</span>
                                            </div>
                                        ) : (
                                            <div style={{ width: fluid(48), height: fluid(48), flexShrink: 0 }} />
                                        )}

                                        {/* Status Dot */}
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: fluid(16), height: fluid(48), flexShrink: 0 }}>
                                            <div style={{ width: fluid(16), height: fluid(16), borderRadius: "50%", backgroundColor: dotColor, border: "3px solid #FFFFFF", boxSizing: "border-box" }} />
                                        </div>

                                        {/* Entry Text */}
                                        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: fluid(4) }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: fluid(16) }}>
                                                <div
                                                    className="activity-desc-content"
                                                    style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 500, color: "#191919" }}
                                                    dangerouslySetInnerHTML={{ __html: entry.description || entry.action || '' }}
                                                />
                                            </div>
                                            {subtitle && (
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 400, color: "#AEAEAE" }}>
                                                    {subtitle}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Right Sticky Column */}
            <div style={{ flex: `0 0 ${fluid(598)}`, width: fluid(598), display: "flex", flexDirection: "column", gap: fluid(24) }}>
                {/* This Week Card */}
                <div style={{ width: "100%", backgroundColor: "#FFFFFF", borderRadius: fluid(24), padding: fluid(24), boxSizing: "border-box", display: "flex", flexDirection: "column", gap: fluid(24) }}>
                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(20), fontWeight: 500, color: "#191919" }}>
                        This week
                    </span>
                    <div style={{ display: "flex", flexDirection: "column", gap: fluid(16) }}>
                        {(() => {
                            if (isStaffRoute) {
                                const statsData = activityStats?.data ?? activityStats;
                                const target = (statsData?.thisWeek && typeof statsData.thisWeek === 'object') ? statsData.thisWeek : statsData;

                                const getVal = (keys, fallback = 0) => {
                                    if (Array.isArray(statsData)) {
                                        const found = statsData.find((item) => {
                                            const label = String(item.label || item.name || item.title || item.type || '').toLowerCase().replace(/[\s_-]/g, '');
                                            return keys.some((k) => k.toLowerCase().replace(/[\s_-]/g, '') === label);
                                        });
                                        if (found) return found.val ?? found.value ?? found.count ?? fallback;
                                    }
                                    if (target && typeof target === 'object') {
                                        for (const k of keys) {
                                            if (target[k] !== undefined && target[k] !== null) return target[k];
                                        }
                                        const lowerKeys = keys.map((k) => k.toLowerCase().replace(/[\s_-]/g, ''));
                                        for (const [propKey, propVal] of Object.entries(target)) {
                                            const normalized = propKey.toLowerCase().replace(/[\s_-]/g, '');
                                            if (lowerKeys.includes(normalized) && propVal !== undefined && propVal !== null) {
                                                return propVal;
                                            }
                                        }
                                    }
                                    return fallback;
                                };

                                const logins = getVal(["logins", "loginCount", "login", "loginsCount", "totalLogins"]);
                                const classScheduled = getVal(["classScheduled", "classesScheduled", "class_scheduled", "classes_scheduled", "classCount", "classesCount", "scheduledClasses", "classes"]);
                                const testScheduled = getVal(["testScheduled", "testsScheduled", "test_scheduled", "tests_scheduled", "testCount", "testsCount", "scheduledTests", "tests"]);
                                const profileEdits = getVal(["profileEdits", "profile_edits", "profileEditCount", "profileEdit", "edits", "profileEdited"]);

                                const staffRows = isTeacher
                                    ? [
                                        { label: "Logins", val: logins },
                                        { label: "Class scheduled", val: classScheduled },
                                        { label: "Test scheduled", val: testScheduled },
                                        { label: "Profile edits", val: profileEdits },
                                    ]
                                    : [
                                        { label: "Logins", val: logins },
                                        { label: "Profile edits", val: profileEdits },
                                    ];

                                return staffRows.map((row, idx) => (
                                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: fluid(20) }}>
                                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#666666", fontWeight: 400 }}>{row.label}</span>
                                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#1A1A1A", fontWeight: 400 }}>{row.val}</span>
                                    </div>
                                ));
                            }

                            const statsData = activityStats?.data ?? activityStats;
                            if (Array.isArray(statsData)) {
                                return statsData.map((item, idx) => (
                                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: fluid(20) }}>
                                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#666666", fontWeight: 400 }}>
                                            {item.label || item.name || item.title || item.type || "Activity"}
                                        </span>
                                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#1A1A1A", fontWeight: 400 }}>
                                            {item.val ?? item.value ?? item.count ?? 0}
                                        </span>
                                    </div>
                                ));
                            }
                            const target = (statsData?.thisWeek && typeof statsData.thisWeek === 'object') ? statsData.thisWeek : statsData;
                            const logins = target?.logins ?? target?.loginCount ?? target?.login ?? target?.["Logins"] ?? 0;
                            const testsSubmitted = target?.testsSubmitted ?? target?.tests_submitted ?? target?.tests ?? target?.testSubmitted ?? target?.testsCount ?? target?.["Tests submitted"] ?? 0;
                            const payments = target?.payments ?? target?.paymentCount ?? target?.payment ?? target?.["Payments"] ?? 0;
                            const profileEdits = target?.profileEdits ?? target?.profile_edits ?? target?.profileEditCount ?? target?.profileEdit ?? target?.["Profile edits"] ?? 0;

                            const rows = [
                                { label: "Logins", val: logins },
                                { label: "Tests submitted", val: testsSubmitted },
                                { label: "Payments", val: payments },
                                { label: "Profile edits", val: profileEdits },
                            ];

                            return rows.map((row, idx) => (
                                <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: fluid(20) }}>
                                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#666666", fontWeight: 400 }}>{row.label}</span>
                                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#1A1A1A", fontWeight: 400 }}>{row.val}</span>
                                </div>
                            ));
                        })()}
                    </div>
                </div>

                {/* Devices Card */}
                <div style={{ width: "100%", backgroundColor: "#FFFFFF", borderRadius: fluid(24), padding: fluid(24), boxSizing: "border-box", display: "flex", flexDirection: "column", gap: fluid(20) }}>
                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(20), fontWeight: 500, color: "#191919" }}>
                        Devices
                    </span>
                    {(() => {
                        const devices = (activityStats?.data ?? activityStats)?.devices;
                        if (!Array.isArray(devices) || devices.length === 0) {
                            return (
                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#AEAEAE", fontWeight: 400 }}>
                                    No devices found
                                </span>
                            );
                        }
                        return (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: fluid(16) }}>
                                {devices.map((device, idx) => {
                                    const { icon, bg } = getDeviceVisual(device?.platform);
                                    const lastUsed = moment(device?.lastUsedAt);
                                    return (
                                        <div key={`${device?.label || 'device'}-${idx}`} style={{ flex: `1 1 calc(50% - ${fluid(8)})`, minWidth: 0, backgroundColor: "#F8F8F8", borderRadius: fluid(16), padding: fluid(16), display: "flex", gap: fluid(12), alignItems: "center", boxSizing: "border-box", height: fluid(77) }}>
                                            <div style={{ width: fluid(45), height: fluid(45), borderRadius: fluid(12), backgroundColor: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                <img src={icon} alt="" style={{ width: fluid(24), height: fluid(24) }} />
                                            </div>
                                            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#1A1A1A", fontWeight: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {device?.label || "Unknown device"}
                                                </span>
                                                {device?.lastUsedAt && lastUsed.isValid() && (
                                                    <span
                                                        // Full text as a native tooltip, only when the ellipsis is actually cutting it off.
                                                        onMouseEnter={(e) => {
                                                            const el = e.currentTarget;
                                                            el.title = el.scrollWidth > el.clientWidth ? el.textContent : '';
                                                        }}
                                                        style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#666666", fontWeight: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                                    >
                                                        Last used {lastUsed.fromNow()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
};

export default ActivityTab;
